import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getDatabase, ref, set, get, update, push, onValue, remove, off } from "firebase/database";
import { v4 as uuidv4 } from 'uuid';

// Firebase configuration
// firebase doesn't like arrays 🤨.

const firebaseConfig = {

  apiKey: process.env.REACT_APP_API_KEY,

  authDomain: process.env.REACT_APP_AUTH_DOMAIN,

  databaseURL: process.env.REACT_APP_DATABASE_URL,

  projectId: process.env.REACT_APP_PROJECT_ID,

  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,

  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,

  appId: process.env.REACT_APP_APP_ID

};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getDatabase(app);

// This isn't necessarily a Firebase thing. Consider moving to lib/utils
const generateUniqueLink = (listID) => {
  const uniqueString = uuidv4();
  return `${listID}-${uniqueString}`;
}

const requireAuthUser = () => {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("You must be signed in!");
  }

  return user;
}

export const writeNewUser = async (email, name) => {
  // Get the authenticated user's UID
  const userID = auth.currentUser?.uid;

  if (!userID) {
    throw new Error("User ID is not available yet.");
  }

  const today = new Date();
  const yyyy = today.getFullYear();
  let mm = today.getMonth() + 1;
  let dd = today.getDate();

  if (dd < 10) {
    dd = '0' + dd;
  }

  if (mm < 10) {
    mm = '0' + mm;
  }

  const joinDate = mm + '/' + dd + '/' + yyyy;

  const listsRef = ref(db, 'lists');
  const newListsId = push(listsRef).key;
  const collabRef = ref(db, 'collab');
  const newCollabId = push(collabRef).key;

  const newSignUp = {
    name: name,
    email: email,
    listsId: newListsId,
    collabId: newCollabId,
    joined: joinDate
  };

  const updates = {};
  updates[`users/${userID}`] = newSignUp;

  try {
    await update(ref(db), updates);
    return newListsId;
  } catch (error) {
    throw error;
  }
}

export const addMovieToList = async (listId, movieData) => {
  const listRef = ref(db, `list/${listId}`);
  const updates = {};
  try {
    const snapshot = await get(listRef);
    if (snapshot.exists()) {
      const newMoviesRef = push(listRef);
      const newMoviesRefID = newMoviesRef.key;

      updates[`movies/${listId}/${newMoviesRefID}`] = movieData;

      return update(ref(db), updates)
      .then(() => console.log("Movie added succesfully"))
      .catch((error) => console.error("Error writing movie:", error));

    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching lists:', error);
  }
}

export const deleteMovie = async (listId, movieId) => {
  const movieRef = ref(db, `movies/${listId}`);

  try {
    const snapshot = await get(movieRef);
    if (snapshot.exists()) {

      const movieToDelete = ref(db, `movies/${listId}/${movieId}`);
      remove(movieToDelete)
    }
  } catch (error) {
    console.error('Error fetching lists:', error);
  }
}

export const deleteList = async (listsId, listId) => {
  try {
    const listData = await fetchUserMovies(listId);

    if (listData) {
      const moviesToDelete = ref(db, `movies/${listId}`);
      remove(moviesToDelete);
    }

    const listToDeleteLists = ref(db, `lists/${listsId}/${listId}`);
    const listToDeleteList = ref(db, `list/${listId}`);
    remove(listToDeleteList);
    remove(listToDeleteLists);
  } catch (error) {
    console.error(error);
  }
}

export const deleteNonOwnedList = async (collabId, listId, userId) => {
  const collabRef = ref(db, `collab/${collabId}/${listId}`);
  const listRef = ref(db, `list/${listId}/allowedUsers/${userId}`);
  
  await Promise.all([
    remove(collabRef),
    remove(listRef)
  ]);
}

// Am I using this anywhere?
export const readUserData = (path) => {
  return get(ref(db, path)).then((snapshot) => {
    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      return null;
    }
  }).catch((error) => {
    console.error("6.6 Error reading data:", error);
  });
};

// ToDo: I need to make it so that this function checks if the user has <10 lists.

export const isAtListsLimit = async (listsId) => {
  const listsRef = ref(db, `lists/${listsId}`);

  try {
    const snapshot = await get(listsRef);

    if (snapshot.exists()) {
      const listsData = snapshot.val();
      let count = 0;
      for (const id of Object.entries(listsData)) {
        count++;
        console.log("list id: ", id);
      }
      console.log("count: ", count);
      
      if (count >= 9) {
        return true;
      } else {
        return false;
      }
    }
  } catch(err) {
    console.error(err);
  }
}

export const addNewList = async (listName) => {
  const user = requireAuthUser();
  const userData = await getUserData(user.uid);

  if (!userData.listsId) {
    throw new Error("User profile is missing ListsId!");
  }

  const userId = user.uid;
  const listsId = userData.listsId;
  const listReference = ref(db, `list`);

  // Push generates a new listID
  const newListRef = push(listReference);
  const newListID = newListRef.key;
  const sharedLink = generateUniqueLink(newListID);


  const today = new Date();
  const yyyy = today.getFullYear();
  let mm = today.getMonth() + 1;
  let dd = today.getDate();

  if (dd < 10) {
    dd = '0' + dd;
  }

  if (mm < 10) {
    mm = '0' + mm;
  }

  const listCreationDate = mm + '/' + dd + '/' + yyyy;
  const newListData = {
    listName: listName,
    sharedLink: sharedLink,
    listCreationDate: listCreationDate
  }

  const updates = {};

  updates[`list/${newListID}/owner`] = userId;
  updates[`lists/${listsId}/${newListID}`] = newListData;
  updates[`list/${newListID}/allowedUsers/${userId}`] = true;
 
  // Pass root reference, avoid extra nesting
  return update(ref(db), updates)
  .then(() => console.log('List created successfully!'))
  .catch((error) => console.error('Error: ', error))
}

export const fetchUserProfile = async (userId) => {
  const userRef = ref(db, `users/${userId}`);

  try {
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      // This is the userProfile dude.
      return snapshot.val();
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching user profile", error);
    return null;
  }
}


export const fetchUserMovies = async (listId) => {
  const movieRef = ref(db, `movies/${listId}`);

  try {
    // Get listID located in users
    const snapshot = await get(movieRef);
    if (!snapshot.exists()) {
      return {};
    }

    const moviesSnap = await get(movieRef);

    if (!moviesSnap) {
      return {};
    }

    const userMovies = moviesSnap.val();
    return userMovies;

    
  } catch (error) {
    console.error("Error fetching movies:", error);
    return [];
  }
};

export const fetchUserMoviesRealTime = (listId, setMoviesList) => {
  const movieRef = ref(db, `movies/${listId}`);

  onValue(
    movieRef,
    async (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setMoviesList({});
        return;
      }

      setMoviesList(data);
    },
    (error) => {
      console.error("fetchUserMovieesRealTime error: ", error);
    }
  )

  return () => off(movieRef);
}

export const fetchUserListRealTime = (listsId, setMovieList) => {
  const listsRef = ref(db, `lists/${listsId}`);

    onValue(
      listsRef,
      async (snapshot) => {
        const data = snapshot.val();

        if (!data) {
          setMovieList({});
          return;
        }

        setMovieList(data);
      },
      (error) => {
        console.error("fetchUserListRealTime error: ", error);
      }
    )

    return () => off(listsRef);
}

// Am I using this anywhere? 01/2026 update: no I am not
export const getList = async (listsId) => {
  const userRef = ref(db, `lists/${listsId}`);
  
  try {
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      const listIds = snapshot.val();
      const listPromises = Object.keys(listIds).map((listId) =>
        get(ref(db, `lists/${listsId}/${listId}`))
      );

      const listSnapshots = await Promise.all(listPromises);

      // This puts the listId and listName into lists.
      const lists = listSnapshots.map((snap, index) => {
        const data = snap.val();
        return {
          listId: Object.keys(listIds)[index], // Associate the listId with the data
          ...data,
        };
      });


      return lists;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching lists:', error);
    return [];
  }
};

export const getListOwner = async (listId) => {
  const listRef = ref(db, `list/${listId}/owner`);

  try {
    const snapshot = await get(listRef);

    if (snapshot.exists()) {
      const user = snapshot.val();
      const listsId = ref(db, `users/${user}/listsId`);
      const secondSnap = await get(listsId);
      if (secondSnap.exists()) {
        return secondSnap.val();
      }
    }
  } catch (error) {
    console.error(error);
  }
}

export const getListName = async (listsId, listId) => {
  const userListsId = await getListOwner(listId); // What am I doing here?
  const listsRef = ref(db, `lists/${userListsId}/${listId}/listName`);
  const listRef = ref(db, `list/${listId}`);

  try {
    const snapshot = await get(listsRef);
    const snaps = await get(listRef);

    if (snapshot.exists()) {
      const listName = snapshot.val();
      const data = snaps.val();

      if (!data.listCreationDate) {
        // Then today...
        const today = new Date();
        const yyyy = today.getFullYear();
        let mm = today.getMonth() + 1;
        let dd = today.getDate();

        if (dd < 10) {
          dd = '0' + dd;
        }

        if (mm < 10) {
          mm = '0' + mm;
        }

        const listCreationDate = mm + '/' + dd + '/' + yyyy;
        update(listRef, {listCreationDate: listCreationDate});
      }

      return listName;
    }
  } catch (error) {
    return
  }
}

export const getCollabList = async (collabId) => {
  const collabRef = ref(db, `collab/${collabId}`);
  
  try {
    const snapshot = await get(collabRef);
    if (snapshot.exists()) {
      const listIds = snapshot.val();
      const listPromises = Object.keys(listIds).map((listId) =>
        get(ref(db, `collab/${collabId}/${listId}`))
      );

      const listSnapshots = await Promise.all(listPromises);

      // This puts the listId and listName into lists.
      const lists = listSnapshots.map((snap, index) => {
        const data = snap.val();
        return {
          listId: Object.keys(listIds)[index], // Associate the listId with the data
          ...data,
        };
      });

      return lists;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching lists:', error);
    return [];
  }
};

export const isOwner = async (userId, listId) => {
  const listRef = ref(db, `list/${listId}/owner`);

  try {
    const snapshot = await get(listRef);
    if (snapshot.exists()) {
      const listVals = snapshot.val();
      return listVals === userId;
    } else {
      return false;
    }

  } catch {
    console.log("Error")
  }
}

export const getShareLink = async (listsId, listId) => {
  const listsRef = ref(db, `lists/${listsId}/${listId}`);

  try {
    const snapshot = await get(listsRef);

    if (snapshot.exists()) {
      const shareLinkVal = snapshot.val();
      const shareLinkKey = shareLinkVal.sharedLink;
      return shareLinkKey;
    }
  } catch (error) {
    console.error("Error getting share link:", error)
  }
}

// This will place users into pending request portion of the db.
export const requestListAccess = async (userName, userEmail, userId, listId, collabId) => {
  const results = await getRequestList(listId)
  let requestExists = false;

  Object.values(results).map((request) => {
    if (request.email === userEmail) {
      requestExists = true;
      return;
    }
  })

  if (!requestExists) {
    const pendingRef = ref(db, `list/${listId}/pendingRequests`);
    const newPendingId = push(pendingRef).key;

    const newRequest = {
      userId: userId,
      name: userName,
      email: userEmail,
      list: listId,
      PendingId: newPendingId,
      collabId: collabId
    }

    const updates = {};
    updates[`list/${listId}/pendingRequests/${newPendingId}`] = newRequest;

    return update(ref(db), updates)
    .then(() => console.log("Sucessful Request!"))
    .catch((error) => console.error("Error requesting:", error));
  }
}

export const canAdd = async (userId, listId) => {
  const listRef = ref(db, `list/${listId}/allowedUsers/${userId}`);

  try {
    const snapshot = await get(listRef);
    if (snapshot.exists()) {
      return true;
    }

    return false;

  } catch (error){
    console.error("Error: ", error);
  }
}

export const getRequestList = async (listId) => {
  const listRef = ref(db, `list/${listId}/pendingRequests`);

  try {
    const snapshot = await get(listRef);
    if (snapshot.exists()) {
      const pendingVals = snapshot.val();
      const pendingKeys = Object.keys(pendingVals);
      const pendingRequests = Object.values(pendingVals);
      
      return pendingKeys.map((requestId, index) => ({
        requestId,
        ...pendingRequests[index],
      }));
    } else {
      return [];
    }
  } catch (error) {
    console.log(`Error retrieving pending list for listId ${listId}. Error `, error);
    return [];
  }
}

export const approveUserRequest = async (requestID, listId, pendingList, listsId) => {
  pendingList.map(async ({userId, requestId, collabId}) => {
    const allowedRef = ref(db, `list/${listId}/allowedUsers/${userId}`);
    const pendingRef = ref(db, `list/${listId}/pendingRequests/${requestID}`)
    const collabRef = ref(db, `collab/${collabId}/${listId}`);

    if (requestID === requestId) {
      try {
        await set(allowedRef, true);

        const listNameRef = ref(db, `lists/${listsId}/${listId}/listName`);

        const listNameSnap = await get(listNameRef);
        let listName = "";

        if (listNameSnap.exists()) {
          listName = listNameSnap.val();
        }

        const collabData = {
          listName: listName
        }

        const updates = {};
        updates[`collab/${collabId}/${listId}`] = collabData;

        update(ref(db), updates);
        await remove(pendingRef);
      } catch (error) {
        console.error("Error approving user: ", error);
      }
    }
  });
}

export const denyUserRequest = async (requestID, listId, pendingList) => {
  pendingList.map(async ({requestId}) => {
    const pendingRef = ref(db, `list/${listId}/pendingRequests/${requestID}`)
    if (requestID === requestId) {
      try {
        await remove(pendingRef);
      } catch (error) {
        console.error("Error removing user: ", error);
      }
    }
  });
}

export const ensureUserTheme = async (userId) => {
  const userRef = ref(db, `users/${userId}`);
  const snapshot = await get(userRef);

  if (snapshot.exists()) {
    const userData = snapshot.val();
    if (!userData.theme) {
      await update(userRef, { theme: "original_light" });
      return "original_light";
    }
    return userData.theme;
  } else {
    console.warn(`⚠️ User ${userId} not found when ensuring theme.`);
    return "original_light";
  }
};

export const applyUserTheme = async (userId) => {
  const userRef = ref(db, `users/${userId}`);
  
  try {
    const snapshot = await get(userRef);

    if (snapshot.exists()) {
      const userData = snapshot.val();
      const savedTheme = userData.theme || "original_light";
      return savedTheme;
    }
  } catch (err) {
    console.error("Error fetching user theme:", err);
    return "original_light";
  }
};

export const getUserName = async (userId) => {
  const userRef = ref(db, `users/${userId}/name`);
  try {
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      const username = snapshot.val();
      return username;
    }
  } catch (err) {
    console.error("Error fetching username.", err);
    return "";
  }
}

// This is acutually turning into a getListInfo kinda thing
export const getListUsers = async (listId) => {
  const listRef = ref(db, `list/${listId}/allowedUsers`);

  try {
    const snapshot = await get(listRef);

    if (snapshot.exists()) {
      const listData = snapshot.val();
      const usernames = [];
      for (const [userId, isAllowed] of Object.entries(listData)) {
        const username = await getUserName(userId);
        usernames.push(username);
      }

      return usernames;
    }
  } catch (err) {
    console.error("Error getting list users", err);
    return "";
  }
}

export const getListsInfo = async (listsId, listId) => {
  const listsRef = ref(db, `lists/${listsId}/${listId}`);
  try {
    const snapshot = await get(listsRef);
    if (snapshot.exists()) {
      const listsData = snapshot.val();
      if (!listsData.listCreationDate) {
        // Insert date info
        const today = new Date();
        const yyyy = today.getFullYear();
        let mm = today.getMonth() + 1;
        let dd = today.getDate();

        if (dd < 10) {
          dd = '0' + dd;
        }

        if (mm < 10) {
          mm = '0' + mm;
        }

        const listCreationDate = mm + '/' + dd + '/' + yyyy;

        await update(listsRef, {listCreationDate: listCreationDate});

      }
      return listsData;
    }
  } catch (err) {
    console.error("Error getting lists info", err);
  }
}

export const toggleMovieStruck = async (listId, movieId, newValue) => {
  const movieRef = ref(db, `movies/${listId}/${movieId}`);
  
  try {
    await update(movieRef, { struck: newValue});
  } catch (err) {
    console.error('Error updating movie toggle', err);
  }
}

export const getUserData = async (userId) => {
	const userRef = ref(db, `users/${userId}`);

	try {
		const snapshot = await get(userRef);
		if (snapshot.exists()) {
			const userData = snapshot.val();
			return userData;
		}
	} catch (err) {
	  console.error("Error while getting user data.", err);
		return {};
	}
}

/**
 * Submits a bug report to the database.
 * @param {string} username - The username of the reporter.
 * @param {string} bugText - The bug description.
 * @returns {Promise<void>} Resolves when the bug is submitted.
 */
export const submitBug = async (username, bugText) => {
  try {
    const bugRef = ref(db, 'bugs');
    const newBugId = push(bugRef).key;
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const submitDate = `${yyyy}-${mm}-${dd}`;
    const newBugSubmit = {
      username,
      text: bugText,
      submitDate
    };

    const updates = {};
    updates[`bugs/${newBugId}`] = newBugSubmit;

    await update(ref(db), updates);
    console.log('Bug submitted successfully!');
  } catch (error) {
    console.error('Error submitting bug:', error);
    throw error;
  }
}